// ─────────────────────────────────────────────────────────
// layouts/nav/navData.ts — Data, types, and selectors for
// the C3 sidebar (icon rail + rich flyout).
//
// Pure TypeScript — no JSX, no React imports.
// Icons are described as path data objects and rendered
// by IconRail / NavFlyout using their SBIcon helper.
// ─────────────────────────────────────────────────────────
import type {
  Task, Milestone, Receivable, PurchaseOrder,
  BankReconEntry, Project, Expense, Employee, TDSEntry, Vendor,
} from '@/types'
import type { DepMapKey } from '@/utils/deps'

// ── Icon path data ───────────────────────────────────────
// d / extra: 1–2 path elements (most icons)
// complex: raw SVG inner HTML for icons using rect / circle / 3+ paths
export interface SBIconDef {
  d?:       string
  extra?:   string
  complex?: string
}

// Module icons keyed by route slug.
// 8 paths updated per design handoff (boq, milestones, tender, receivables,
// retention, pettycash, quotations, invoices).
export const MODULE_ICONS: Record<string, SBIconDef> = {
  // ── Overview ──────────────────────────────────────────
  dashboard:     { complex: '<rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>' },
  projects:      { complex: '<path d="M1 4h14v9a1 1 0 01-1 1H2a1 1 0 01-1-1V4z"/><path d="M1 4l2-3h10l2 3"/><path d="M6 8h4"/>' },
  // BOQ → document with bullet-list column (itemised quantities)
  boq:           { complex: '<path d="M3 1h10a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z"/><path d="M6 5h6M6 8.5h6M6 12h6"/><circle cx="4" cy="5" r=".8" fill="currentColor"/><circle cx="4" cy="8.5" r=".8" fill="currentColor"/><circle cx="4" cy="12" r=".8" fill="currentColor"/>' },
  milestones:    { d: 'M3.5 1v14',            extra: 'M3.5 2h9l-2 2.6 2 2.6h-9' },
  // Daily DPR → clipboard with notes (daily progress report on a board)
  daily:         { complex: '<rect x="5.5" y="1" width="5" height="2.5" rx="1"/><path d="M3 2.5h10a1 1 0 011 1v11a1 1 0 01-1 1H3a1 1 0 01-1-1v-11a1 1 0 011-1z"/><path d="M5 7h6M5 10h6M5 13h4"/>' },
  planner:       { d: 'M2 3h12v10H2z',        extra: 'M5 6l2 2 4-4M5 10h4' },
  // ── Tenders & Billing ─────────────────────────────────
  // Tender → document with official approval seal in corner
  tender:        { complex: '<path d="M3 1h10a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z"/><path d="M5 5h6M5 8h4"/><circle cx="10" cy="12" r="2.5"/><path d="M8.8 12l.8.8 1.8-1.8"/>' },
  // EMD → padlock (security deposit = locked funds)
  emd:           { complex: '<rect x="4" y="7" width="8" height="7" rx="1"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2"/><circle cx="8" cy="11" r="1.2"/>' },
  receivables:   { d: 'M1 6h14v7a1 1 0 01-1 1H2a1 1 0 01-1-1V6z', extra: 'M8 1v4.5M5.5 3.5L8 6l2.5-2.5' },
  // Retention → percentage box (% of payment withheld)
  retention:     { complex: '<rect x="2" y="2" width="12" height="12" rx="1"/><path d="M5 11L11 5"/><circle cx="5.5" cy="5.5" r="1.5"/><circle cx="10.5" cy="10.5" r="1.5"/>' },
  einvoice:      { complex: '<path d="M3 1h7l4 4v9a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z"/><path d="M10 1v5h5M5 8h6M5 11h4"/><circle cx="12" cy="12" r="3"/><path d="M11 12l.8.8L13.5 11"/>' },
  // ── Finance ───────────────────────────────────────────
  expenses:      { d: 'M1 5h14v8a1 1 0 01-1 1H2a1 1 0 01-1-1V5z', extra: 'M1 5l2-4h10l2 4' },
  // Cashflow → bidirectional horizontal arrows (money in / money out)
  cashflow:      { complex: '<path d="M4 5.5h8"/><path d="M9 3.5l3 2-3 2"/><path d="M12 10.5H4"/><path d="M7 8.5l-3 2 3 2"/>' },
  // Profitability → ascending bar chart (growing profit)
  profitability: { complex: '<path d="M2 13h3V9H2zM6 13h3V6H6zM10 13h3V3h-3z"/><path d="M1 13h14"/>' },
  pettycash:     { d: 'M2.5 4c0-1.2 2-2 4.5-2s4.5.8 4.5 2-2 2-4.5 2S2.5 5.2 2.5 4z', extra: 'M2.5 4v3.5c0 1.2 2 2 4.5 2s4.5-.8 4.5-2V4M2.5 7.5V11c0 1.2 2 2 4.5 2s4.5-.8 4.5-2V7.5' },
  // BankRecon → two ledger panels with connecting lines (matching entries)
  bankrecon:     { complex: '<rect x="1" y="3" width="5.5" height="10" rx="1"/><rect x="9.5" y="3" width="5.5" height="10" rx="1"/><path d="M6.5 6.5h3M6.5 9.5h3M6.5 12.5h3"/>' },
  // ── Procurement ───────────────────────────────────────
  vendors:       { d: 'M2 6h12v7a1 1 0 01-1 1H3a1 1 0 01-1-1V6z', extra: 'M8 1v5M5 3l3-2 3 2' },
  po:            { d: 'M3 1h10a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z', extra: 'M5 5h6M5 8h4M9 11l1.5 1.5L13 10' },
  // Quotations → speech bubble with text lines
  quotations:    { complex: '<path d="M2 2h12a1 1 0 011 1v7a1 1 0 01-1 1H9l-1 3-1-3H2a1 1 0 01-1-1V3a1 1 0 011-1z"/><path d="M4 6h8M4 9h5"/>' },
  invoices:      { d: 'M3 1h10a1 1 0 011 1v13l-2-1.2-2 1.2-2-1.2-2 1.2-2-1.2V2a1 1 0 011-1z', extra: 'M5 5h6M5 8h4' },
  // ── HR & Payroll ──────────────────────────────────────
  employees:     { complex: '<circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>' },
  attendance:    { complex: '<rect x="1" y="2" width="14" height="13" rx="1"/><path d="M5 1v3M11 1v3M1 7h14"/><path d="M5 10l2 2 4-4"/>' },
  // Salary → banknote shape (wages / payment concept)
  salary:        { complex: '<rect x="1" y="5" width="14" height="8" rx="1"/><circle cx="8" cy="9" r="2"/><path d="M3 7h2M3 11h2M11 7h2M11 11h2"/>' },
  // Slips → perforated receipt (salary slip with tear-off edge)
  slips:         { complex: '<path d="M3 1h10a1 1 0 011 1v9"/><path d="M3 1a1 1 0 00-1 1v9"/><path d="M2 11l1.5 1.5L5 11l1.5 1.5L8 11l1.5 1.5L11 11l1.5 1.5L14 11"/><path d="M5 5h6M5 8h4"/>' },
  // Advances → coin with upward arrow (money given in advance)
  advances:      { complex: '<circle cx="8" cy="12" r="2.2"/><path d="M8 9.8V4"/><path d="M5.5 6.5L8 4l2.5 2.5"/>' },
  // PF/ESIC → vault safe (secured provident fund savings)
  pfreg:         { complex: '<rect x="2" y="2" width="12" height="12" rx="1.5"/><circle cx="7.5" cy="8" r="2.8"/><path d="M7.5 5.2v.8M7.5 10v.8M4.7 8h.8M9.5 8h.8"/><path d="M14 6.5v3"/>' },
  // Insurance → umbrella (protection / coverage concept)
  insurance:     { d: 'M2 9 A6 6 0 0 0 14 9', extra: 'M8 3.5V13C8 14.5 11 14.5 11 13' },
  // ── Compliance ────────────────────────────────────────
  tds:           { complex: '<rect x="1" y="2" width="14" height="12" rx="1"/><path d="M5 6h6M5 9h4"/><path d="M11 9l1.5 1.5L15 8"/>' },
  // AuditLog → magnifying glass (search / examine records)
  auditlog:      { complex: '<circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5l4 4"/>' },
  // ── Settings ──────────────────────────────────────────
  users:         { complex: '<circle cx="6" cy="5" r="2.5"/><path d="M1 13c0-2.8 2.2-5 5-5"/><circle cx="12" cy="5" r="2"/><path d="M12 8c1.7 0 3 1.3 3 3"/>' },
  billing:       { complex: '<rect x="1" y="4" width="14" height="9" rx="1"/><path d="M1 8h14M5 12h3"/>' },
  branding:      { complex: '<circle cx="5.5" cy="6.5" r="1.5"/><path d="M1 14l5-5 3 3 2-2 4 4"/><rect x="1" y="1" width="14" height="14" rx="2"/>' },
  bulkimport:    { d: 'M8 2v9M5 8l3 3 3-3M3 13h10' },
  bulkexport:    { d: 'M8 14V5M5 11l3 3 3-3M3 2h10' },
  notifications: { d: 'M8 2a5 5 0 00-5 5v3l-1 2h12l-1-2V7a5 5 0 00-5-5z', extra: 'M6.5 13.5a1.5 1.5 0 003 0' },
  // Reports → document with embedded line chart
  reports:       { complex: '<path d="M3 1h7l4 4v9a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z"/><path d="M10 1v4h4"/><path d="M5 9l2-2 2 1.5 2-3"/>' },
  settings:      { complex: '<circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3"/>' },
  // ── Super Admin ───────────────────────────────────────
  'ac-companies':     { d: 'M1 14V5a1 1 0 011-1h12a1 1 0 011 1v9M1 14h14' },
  // ac-overview → trend line (platform analytics at a glance)
  'ac-overview':      { complex: '<path d="M2 11l4-6 3 4 3-2 3-4"/><path d="M2 13h12"/>' },
  'ac-subscriptions': { d: 'M1 4h14v9a1 1 0 01-1 1H2a1 1 0 01-1-1V4z', extra: 'M1 4l2-3h10l2 3' },
}

// Category rail icons — unique per category, NOT reused from module icons
export const CATEGORY_ICONS: Record<string, SBIconDef> = {
  // House — "home / overview" concept
  overview:      { d: 'M1 7.5L8 2l7 5.5V14a1 1 0 01-1 1H2a1 1 0 01-1-1V7.5z', extra: 'M6 15V9h4v6' },
  // Receipt with lines — "billing / contracts" concept
  tenders:       { d: 'M3 1h10v13l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5V1z', extra: 'M6 5h5M6 8h3' },
  // Wallet with coin slot — "money / finance" concept
  finance:       { d: 'M2 5h12a1 1 0 011 1v7a1 1 0 01-1 1H2a1 1 0 01-1-1V6a1 1 0 011-1z', extra: 'M2 9h14M10 11h2' },
  // Shopping bag — "buying / sourcing" concept
  procurement:   { complex: '<path d="M3 5h10l1 9H2L3 5z"/><path d="M6 5V4a2 2 0 014 0v1"/>' },
  // Two people — "workforce / HR" concept
  hr:            { complex: '<circle cx="6" cy="5" r="2.5"/><path d="M1 14c0-2.8 2.2-5 5-5h2c2.8 0 5 2.2 5 5"/><circle cx="13" cy="6" r="2"/><path d="M11 14c.5-2 1.6-4 3.5-4"/>' },
  // Balance scale — "law / compliance" concept
  compliance:    { complex: '<path d="M8 2v12M3 14h10"/><path d="M3 5l5-2 5 2"/><path d="M3 5l-1.5 3.5h3L3 5z"/><path d="M13 5l-1.5 3.5h3L13 5z"/>' },
  // Gear — universally "settings"
  settings:      { complex: '<circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3"/>' },
  // Building — "platform / super admin"
  'super-admin': { d: 'M1 14V5a1 1 0 011-1h12a1 1 0 011 1v9M1 14h14' },
}

// ── Category / module data ───────────────────────────────

export interface NavQuickAction {
  label:      string
  route:      string
  depModule?: DepMapKey  // compile-time validated against DEP_MAP keys
}

export interface NavModuleItem {
  pg:      string   // route slug
  label:   string
  maOnly?: boolean  // hidden unless isMasterAdmin()
}

export interface NavCategory {
  id:           string
  label:        string
  isFooter?:    boolean       // Settings + Super Admin go in rail footer
  paOnly?:      boolean       // whole section hidden unless isPlatformAdmin()
  pinnedLabel:  string        // flyout Zone 2 heading
  quickActions: NavQuickAction[]
  modules:      NavModuleItem[]
}

export const NAV_CATEGORIES: NavCategory[] = [
  {
    id: 'overview', label: 'Overview',
    pinnedLabel: 'Pinned projects',
    quickActions: [
      { label: '+ Project', route: '/projects' },
      { label: '+ Task',    route: '/planner',  depModule: 'tasks' },
    ],
    modules: [
      { pg: 'dashboard',  label: 'Dashboard' },
      { pg: 'projects',   label: 'Projects' },
      { pg: 'boq',        label: 'BOQ Tracker' },
      { pg: 'milestones', label: 'Milestones' },
      { pg: 'daily',      label: 'DPR' },
      { pg: 'planner',    label: 'Tasks / Planner' },
    ],
  },
  {
    id: 'tenders', label: 'Tenders & Billing',
    pinnedLabel: 'Active bills',
    quickActions: [
      { label: '+ Bill',   route: '/receivables', depModule: 'receivables' },
      { label: '+ Tender', route: '/tender' },
    ],
    modules: [
      { pg: 'tender',      label: 'Tenders & CS' },
      { pg: 'emd',         label: 'EMD Tracker' },
      { pg: 'receivables', label: 'Receivables' },
      { pg: 'retention',   label: 'Retention Release' },
      { pg: 'einvoice',    label: 'GST e-Invoice' },
    ],
  },
  {
    id: 'finance', label: 'Finance',
    pinnedLabel: 'Recent expenses',
    quickActions: [
      { label: '+ Expense',    route: '/expenses',  depModule: 'expenses' },
      { label: '+ Petty cash', route: '/pettycash', depModule: 'pettycash' },
    ],
    modules: [
      { pg: 'expenses',      label: 'Expenses' },
      { pg: 'cashflow',      label: 'Cash Flow' },
      { pg: 'profitability', label: 'Profitability' },
      { pg: 'pettycash',     label: 'Petty Cash' },
      { pg: 'bankrecon',     label: 'Bank Reconciliation' },
    ],
  },
  {
    id: 'procurement', label: 'Procurement',
    pinnedLabel: 'Active POs',
    quickActions: [
      { label: '+ PO',      route: '/po',       depModule: 'po' },
      { label: '+ Invoice', route: '/invoices', depModule: 'invoices' },
    ],
    modules: [
      { pg: 'vendors',    label: 'Vendors' },
      { pg: 'po',         label: 'Purchase Orders' },
      { pg: 'quotations', label: 'Quotations' },
      { pg: 'invoices',   label: 'Contractor Invoices' },
    ],
  },
  {
    id: 'hr', label: 'HR & Payroll',
    pinnedLabel: 'Active employees',
    quickActions: [
      { label: '+ Employee', route: '/employees' },
      { label: '+ Advance',  route: '/advances', depModule: 'advances' },
    ],
    modules: [
      { pg: 'employees',  label: 'Employees' },
      { pg: 'attendance', label: 'Attendance' },
      { pg: 'salary',     label: 'Salary' },
      { pg: 'slips',      label: 'Salary Slips' },
      { pg: 'advances',   label: 'Advances' },
      { pg: 'pfreg',      label: 'PF & ESIC' },
      { pg: 'insurance',  label: 'Insurance' },
    ],
  },
  {
    id: 'compliance', label: 'Compliance',
    pinnedLabel: 'TDS entries',
    quickActions: [
      { label: '+ TDS', route: '/tds', depModule: 'tds' },
    ],
    modules: [
      { pg: 'tds',      label: 'TDS Ledger' },
      { pg: 'auditlog', label: 'Audit Log' },
    ],
  },
  {
    id: 'settings', label: 'Settings',
    isFooter: true,
    pinnedLabel: '',
    quickActions: [],
    modules: [
      { pg: 'users',         label: 'Users',        maOnly: true },
      { pg: 'billing',       label: 'Billing' },
      { pg: 'branding',      label: 'Branding',     maOnly: true },
      { pg: 'bulkimport',    label: 'Bulk Import',  maOnly: true },
      { pg: 'bulkexport',    label: 'Bulk Export',  maOnly: true },
      { pg: 'notifications', label: 'Notifications' },
      { pg: 'reports',       label: 'Reports' },
      { pg: 'settings',      label: 'Settings' },
    ],
  },
  {
    id: 'super-admin', label: 'Super Admin',
    isFooter: true,
    paOnly: true,
    pinnedLabel: '',
    quickActions: [],
    modules: [
      { pg: 'ac-companies',     label: 'Companies' },
      { pg: 'ac-overview',      label: 'Platform Overview' },
      { pg: 'ac-subscriptions', label: 'Subscriptions' },
    ],
  },
]

// ── Route → category lookup ──────────────────────────────
export const ROUTE_TO_CATEGORY: Record<string, string> = {
  // Overview
  dashboard: 'overview', projects: 'overview', boq: 'overview',
  milestones: 'overview', daily: 'overview', planner: 'overview',
  // Tenders & Billing
  tender: 'tenders', emd: 'tenders', receivables: 'tenders',
  retention: 'tenders', einvoice: 'tenders',
  // Finance
  expenses: 'finance', cashflow: 'finance', profitability: 'finance',
  pettycash: 'finance', bankrecon: 'finance',
  // Procurement
  vendors: 'procurement', po: 'procurement', quotations: 'procurement',
  invoices: 'procurement',
  // HR & Payroll
  employees: 'hr', attendance: 'hr', salary: 'hr', slips: 'hr',
  advances: 'hr', pfreg: 'hr', insurance: 'hr',
  // Compliance
  tds: 'compliance', auditlog: 'compliance',
  // Settings
  users: 'settings', billing: 'settings', branding: 'settings',
  bulkimport: 'settings', bulkexport: 'settings',
  notifications: 'settings', reports: 'settings', settings: 'settings',
  profile: 'settings',
  // Super Admin
  'ac-companies': 'super-admin', 'ac-overview': 'super-admin',
  'ac-subscriptions': 'super-admin',
}

// ── Badge count helpers (pure functions) ─────────────────
export function countOverdueTasks(tasks: Task[]): number {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  return tasks.filter(t =>
    (t.status === 'Open' || t.status === 'In Progress') &&
    !!t.due && new Date(t.due) < today
  ).length
}

export function countDelayedMilestones(milestones: Milestone[]): number {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  return milestones.filter(m =>
    m.status === 'Delayed' ||
    (m.status !== 'Completed' && !!m.targetDate && new Date(m.targetDate) < today)
  ).length
}

export function countPendingReceivables(recv: Receivable[]): number {
  return recv.filter(r => r.status !== 'Paid').length
}

export function countPendingPOs(pos: PurchaseOrder[]): number {
  return pos.filter(p => p.status === 'Draft' || p.status === 'Approved').length
}

export function countUnmatchedRecon(recon: BankReconEntry[]): number {
  return recon.filter(r => !r.matched).length
}

export function countPendingSalaries(sal: Record<string, { status?: string }>): number {
  return Object.values(sal).filter(s => s.status === 'Pending').length
}

// ── Pinned record type & helpers ─────────────────────────
export interface PinnedRecord {
  id:    string
  label: string
  sub:   string
  route: string
}

// Short amount formatter for compact sidebar display
function fmtShort(n: number): string {
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`
  if (n >= 100_000)    return `₹${(n / 100_000).toFixed(1)}L`
  if (n >= 1_000)      return `₹${(n / 1_000).toFixed(0)}K`
  return `₹${Math.round(n)}`
}

// Auto-surface up to `limit` records per category from store slices
export function getAutoSurfaced(
  categoryId: string,
  store: {
    proj:   Project[]
    recv:   Receivable[]
    exp:    Expense[]
    po:     PurchaseOrder[]
    emp:    Employee[]
    tds:    TDSEntry[]
    vendor: Vendor[]
  },
  limit = 3,
): PinnedRecord[] {
  switch (categoryId) {
    case 'overview':
      return [...store.proj]
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, limit)
        .map(p => ({
          id:    p.id,
          label: p.name,
          sub:   `${p.code || p.client} · ${fmtShort(p.value)}`,
          route: `/projects/${p.id}`,
        }))
    case 'tenders':
      return store.recv
        .filter(r => r.status !== 'Paid')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, limit)
        .map(r => ({
          id:    r.id,
          label: r.billNo,
          sub:   `${r.projName} · ${fmtShort(r.netReceivable)}`,
          route: '/receivables',
        }))
    case 'finance':
      return [...store.exp]
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, limit)
        .map(e => ({
          id:    e.id,
          label: e.desc || e.category,
          sub:   `${e.category} · ${fmtShort(e.amount)}`,
          route: '/expenses',
        }))
    case 'procurement':
      return store.po
        .filter(p => p.status !== 'Cancelled')
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, limit)
        .map(p => ({
          id:    p.id,
          label: p.poNo,
          sub:   `${store.vendor.find(v => v.id === p.vendorId)?.name ?? 'Vendor'} · ${fmtShort(p.grandTotal)}`,
          route: '/po',
        }))
    case 'hr':
      return store.emp
        .filter(e => e.status === 'Active')
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, limit)
        .map(e => ({
          id:    e.id,
          label: e.name,
          sub:   e.designation || e.type,
          route: '/employees',
        }))
    case 'compliance':
      return [...store.tds]
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, limit)
        .map(t => ({
          id:    t.id,
          label: t.challanNo || t.section,
          sub:   `${t.section} · ${fmtShort(t.tdsAmount)}`,
          route: '/tds',
        }))
    default:
      return []
  }
}

// ── Manual pins (localStorage) ───────────────────────────
const pinsKey = (companyId: string) => `cbd_nav_pins_${companyId}`

export function getManualPinIds(companyId: string, categoryId: string): string[] {
  try {
    const s = localStorage.getItem(pinsKey(companyId))
    const all = s ? (JSON.parse(s) as Record<string, string[]>) : {}
    return all[categoryId] ?? []
  } catch {
    return []
  }
}

export function toggleManualPin(companyId: string, categoryId: string, recordId: string): void {
  try {
    const s = localStorage.getItem(pinsKey(companyId))
    const all: Record<string, string[]> = s ? (JSON.parse(s) as Record<string, string[]>) : {}
    const ids = all[categoryId] ?? []
    all[categoryId] = ids.includes(recordId)
      ? ids.filter(id => id !== recordId)
      : [recordId, ...ids].slice(0, 5)   // max 5 manual pins
    localStorage.setItem(pinsKey(companyId), JSON.stringify(all))
  } catch { /* ignore */ }
}
